import { defineMessages } from 'react-intl';

import type {
  CompositeSubField,
  CompositeSubFieldValue,
} from '~/api/sylvanus/typedefs.autogen';
import type { Highlight } from '~/features/document-viewer/types/highlight';

import { getCurrencyCodes } from '~/features/unified-fields/currencyOptions';

export const MONETARY_TEMPLATE_ID = 'monetary';

const MIN_SUBFIELDS = 1;
const MAX_SUBFIELDS = 20;
const MIN_DROPDOWN_OPTIONS = 2;

export type MonetaryCompositeConfig = {
  templateId: typeof MONETARY_TEMPLATE_ID;
  fieldDescription: string;
  subfields: CompositeSubField[];
};

export type CompositeSubfieldIssue =
  | 'count'
  | 'duplicateName'
  | 'dropdownOptions';

export const compositeSubfieldIssueMessages = defineMessages({
  count: {
    id: 'monetaryComposite.subfieldCount',
    defaultMessage: 'A composite field needs 1 to 20 subfields.',
    description:
      'Error when a monetary composite model has too few or too many subfields.',
  },
  duplicateName: {
    id: 'monetaryComposite.duplicateSubfieldName',
    defaultMessage: 'Subfield names must be unique.',
    description:
      'Error when a monetary composite model repeats a subfield name.',
  },
  dropdownOptions: {
    id: 'monetaryComposite.dropdownOptions',
    defaultMessage: 'A dropdown subfield needs at least 2 unique options.',
    description:
      'Error when a monetary currency subfield has fewer than two distinct options.',
  },
});

export function createDefaultMonetaryComposite(
  fieldDescription: string,
): MonetaryCompositeConfig {
  return {
    templateId: MONETARY_TEMPLATE_ID,
    fieldDescription,
    subfields: [
      {
        name: 'amount',
        sub_type: 'float',
        description: 'Numeric amount',
        decimals: 2,
      },
      {
        name: 'currency',
        sub_type: 'dropdown',
        options: getCurrencyCodes(),
        default_option: 'USD',
        required: false,
      },
    ],
  };
}

export type StoredFieldComposite = {
  prompt_model_type?: string;
  template_id?: string;
  field_description?: string;
  subfields?: CompositeSubField[];
};

export function readFieldComposite<TemplateId extends string>(
  config: StoredFieldComposite,
  templateId: TemplateId,
):
  | {
      templateId: TemplateId;
      fieldDescription: string;
      subfields: CompositeSubField[];
    }
  | undefined {
  if (config.prompt_model_type !== 'field_composite') {
    return undefined;
  }
  if (config.template_id !== templateId) {
    return undefined;
  }
  if (!Array.isArray(config.subfields)) {
    return undefined;
  }

  return {
    templateId,
    fieldDescription: config.field_description ?? '',
    subfields: config.subfields,
  };
}

export function readMonetaryComposite(
  config: StoredFieldComposite,
): MonetaryCompositeConfig | undefined {
  return readFieldComposite(config, MONETARY_TEMPLATE_ID);
}

export function getCompositeSubfieldIssue(
  subfields: CompositeSubField[],
): CompositeSubfieldIssue | null {
  if (subfields.length < MIN_SUBFIELDS || subfields.length > MAX_SUBFIELDS) {
    return 'count';
  }

  const names = new Set<string>();
  for (const subfield of subfields) {
    if (names.has(subfield.name)) {
      return 'duplicateName';
    }
    names.add(subfield.name);

    if (subfield.sub_type !== 'dropdown') {
      continue;
    }

    const options = subfield.options ?? [];
    if (
      options.length < MIN_DROPDOWN_OPTIONS ||
      new Set(options).size !== options.length
    ) {
      return 'dropdownOptions';
    }
  }

  return null;
}

const AMOUNT_SUBFIELD = 'amount';
const CURRENCY_SUBFIELD = 'currency';

export function resolveMonetaryComposite(
  composite: MonetaryCompositeConfig | undefined,
): MonetaryCompositeConfig {
  return composite ?? createDefaultMonetaryComposite('');
}

export function getMonetaryCurrencyOptions(
  subfields: CompositeSubField[] | undefined,
): string[] {
  const currency = subfields?.find(
    (subfield) => subfield.name === CURRENCY_SUBFIELD,
  );
  const options = currency?.options ?? [];
  if (options.length >= MIN_DROPDOWN_OPTIONS) {
    return options;
  }

  return (
    createDefaultMonetaryComposite('').subfields.find(
      (subfield) => subfield.name === CURRENCY_SUBFIELD,
    )?.options ?? []
  );
}

export function subTypeFor(
  subfields: CompositeSubField[],
  name: string,
  fallback: CompositeSubField['sub_type'],
): CompositeSubField['sub_type'] {
  return (
    subfields.find((subfield) => subfield.name === name)?.sub_type ?? fallback
  );
}

function unwrapSubfieldValue(entry: unknown): unknown {
  if (entry && typeof entry === 'object' && 'value' in entry) {
    return entry.value;
  }
  return entry;
}

export function readSubfieldScalar(entry: unknown): string {
  const scalar = unwrapSubfieldValue(entry);
  if (typeof scalar === 'number') {
    return String(scalar);
  }
  if (typeof scalar === 'string') {
    return scalar;
  }
  if (!Array.isArray(scalar)) {
    return '';
  }

  return scalar
    .filter((item) => typeof item === 'string' || typeof item === 'number')
    .map(String)
    .join(', ');
}

export function isCompositeValueRecord(
  value: unknown,
): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function hasSubstantiveCompositeValue(
  value: unknown,
  subfields: CompositeSubField[] | undefined,
): boolean {
  if (!isCompositeValueRecord(value)) {
    return false;
  }

  const required = (subfields ?? []).filter(
    (subfield) => subfield.required !== false,
  );
  if (required.length > 0) {
    return required.every(
      (subfield) => readSubfieldScalar(value[subfield.name]) !== '',
    );
  }

  const entries = subfields?.length
    ? subfields.map((subfield) => value[subfield.name])
    : Object.values(value);
  return entries.some((entry) => readSubfieldScalar(entry) !== '');
}

export function formatCompositeFieldValue(value: unknown): string {
  if (!isCompositeValueRecord(value)) {
    return '';
  }

  const amount = readSubfieldScalar(value[AMOUNT_SUBFIELD]);
  const currency = readSubfieldScalar(value[CURRENCY_SUBFIELD]);
  const known = [amount, currency].filter(Boolean);
  if (known.length > 0) {
    return known.join(' ');
  }

  return Object.values(value).map(readSubfieldScalar).filter(Boolean).join(' ');
}

export function readMonetaryCompositeValue(value: unknown): {
  amount: string;
  currency: string;
} {
  if (!isCompositeValueRecord(value)) {
    return { amount: '', currency: '' };
  }

  return {
    amount: readSubfieldScalar(value[AMOUNT_SUBFIELD]),
    currency: readSubfieldScalar(value[CURRENCY_SUBFIELD]),
  };
}

export function buildMonetaryCompositeValue(
  amount: string,
  currency: string,
  subfields: CompositeSubField[],
): { [key: string]: CompositeSubFieldValue } {
  return {
    [AMOUNT_SUBFIELD]: {
      name: AMOUNT_SUBFIELD,
      sub_type: subTypeFor(subfields, AMOUNT_SUBFIELD, 'float'),
      value: amount === '' ? null : amount,
    },
    [CURRENCY_SUBFIELD]: {
      name: CURRENCY_SUBFIELD,
      sub_type: subTypeFor(subfields, CURRENCY_SUBFIELD, 'dropdown'),
      value: currency === '' ? null : currency,
    },
  };
}

export function toMonetaryGoldValue({
  absent = false,
  amount,
  currency,
  composite,
  highlights,
}: {
  absent?: boolean;
  amount?: string;
  currency?: string;
  composite: MonetaryCompositeConfig | undefined;
  highlights: Highlight[] | undefined;
}) {
  const resolved = resolveMonetaryComposite(composite);
  if (!absent && (amount ?? '') === '' && (currency ?? '') === '') {
    if (!highlights?.length) {
      return null;
    }
    return {
      highlights,
      type: 'unknown' as const,
      value: null,
    };
  }
  const nextValue = absent
    ? null
    : buildMonetaryCompositeValue(
        amount ?? '',
        currency ?? '',
        resolved.subfields,
      );

  return {
    highlights,
    subfields: resolved.subfields,
    templateId: resolved.templateId,
    type: 'composite' as const,
    value: nextValue,
  };
}
